/**
 * Team → Email Trust.
 *
 * The classifier's verdict feed, and the human decisions it can't make. The
 * default view is the queue: withheld messages nobody has decided yet.
 *
 * - Release clears the message. The menu says what the gate learns: trust
 *   the sender's address, trust its whole domain (applies only to mail whose
 *   sender check passes), or nothing (a relay or forwarder). A learning
 *   release also clears that sender's other withheld messages.
 * - Confirm agrees with the classifier: the message stays withheld and
 *   leaves the queue. No ledger entry, no routing rule.
 * - Blacklist blocks the sender in the ledger *and* writes a routing rule.
 *
 * The sender ledger below shows what the gate has learned; revoking an entry
 * sends that sender's next message back through the classifier.
 */

import {
  adminApi,
  type EmailReleaseTrust,
  type EmailSenderAuth,
  type EmailSenderTrustRow,
  type EmailTrustFilter,
  type EmailTrustRow,
  type SenderTrustStatus,
} from "../../api/admin";
import { useConfirm } from "../../components/confirm/context";
import { PageHeader } from "../../components/PageHeader";
import { Pill } from "../../components/Pill";
import { QueryBoundary } from "../../components/QueryBoundary";
import { useToast } from "../../components/toast/context";
import { FilterChips } from "../../components/ui/FilterChips";
import { Menu, MenuItem } from "../../components/ui/Menu";
import { useMenuTrigger } from "../../components/ui/useMenuTrigger";
import { verdictTone } from "../../lib/aiTeam";
import { formatRelative } from "../../lib/formatRelative";
import { useUrlFilter } from "../../lib/hooks/useUrlFilter";

const FILTERS: { value: EmailTrustFilter; label: string }[] = [
  { value: "needs_decision", label: "Needs decision" },
  { value: "quarantined", label: "Quarantined" },
  { value: "suspicious", label: "Suspicious" },
  { value: "cleared", label: "Cleared" },
  { value: "all", label: "All" },
];

/** The sender check Exchange Online ran, as a short line under the sender. */
const SENDER_AUTH: Record<EmailSenderAuth, { label: string; className: string }> = {
  verified: { label: "sender verified", className: "text-ok-fg" },
  internal: { label: "internal", className: "text-ok-fg" },
  failed: { label: "sender check failed", className: "text-danger" },
  unknown: { label: "sender unverified", className: "text-ink-low" },
};

function domainOf(sender: string | null | undefined): string | null {
  const at = (sender ?? "").lastIndexOf("@");
  return at >= 0 ? (sender ?? "").slice(at + 1).toLowerCase() : null;
}

export function EmailTrustPage() {
  const [filter, setFilter] = useUrlFilter<EmailTrustFilter>(
    "verdict",
    FILTERS.map((f) => f.value),
    "needs_decision",
  );
  const trustQuery = adminApi.emailTrust.list(filter);
  const releaseMutation = adminApi.emailTrust.release();
  const confirmMutation = adminApi.emailTrust.confirm();
  const blacklistMutation = adminApi.emailTrust.blacklist();
  const toast = useToast();
  const confirm = useConfirm();

  const onRelease = (row: EmailTrustRow, trust: EmailReleaseTrust) => {
    releaseMutation.mutate(
      { id: row.id, trust },
      {
        onSuccess: (result) =>
          toast.ok(
            result.trusted_sender
              ? `Released – ${result.trusted_sender} is now trusted` +
                  (result.also_cleared > 0
                    ? ` (+${result.also_cleared} more cleared)`
                    : "")
              : (result.sender_trust_note ?? "Released"),
          ),
        onError: (e) => toast.error(e),
      },
    );
  };

  const onConfirm = (row: EmailTrustRow) => {
    confirmMutation.mutate(row.id, {
      onSuccess: () => toast.ok(`Kept ${row.verdict} – off the queue`),
      onError: (e) => toast.error(e),
    });
  };

  const onBlacklist = async (row: EmailTrustRow) => {
    const ok = await confirm({
      title: `Blacklist ${row.sender || "this sender"}?`,
      body: "The message is quarantined and future mail from that address is routed to spam.",
      confirmLabel: "Blacklist",
      tone: "danger",
    });
    if (!ok) return;
    blacklistMutation.mutate(row.id, {
      onSuccess: (result) =>
        toast.show(
          result.routing_rule_note ??
            `Blacklisted${result.blocked_sender ? ` — ${result.blocked_sender} is now blocked` : ""}`,
          { tone: result.routing_rule_id ? "ok" : "error" },
        ),
      onError: (e) => toast.error(e),
    });
  };

  return (
    <>
      <PageHeader
        title="Email Trust"
        description="Classifier verdicts on inbound mail the AI employees may read. Release clears a message and can trust its sender or domain; confirm keeps it withheld; blacklist blocks the sender and routes their mail away."
      />

      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <FilterChips
          aria-label="Verdict"
          options={FILTERS}
          value={filter}
          onChange={setFilter}
        />
        <div className="text-sm text-ink-low ml-2">
          <span className="tnum font-medium text-ink">
            {trustQuery.data?.length ?? 0}
          </span>{" "}
          messages
        </div>
      </div>

      <QueryBoundary
        query={trustQuery}
        empty={
          <div className="empty-state">
            <div className="title">Nothing to review</div>
            <p className="hint">
              Messages appear here as the trust gate classifies inbound mail.
            </p>
          </div>
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Sender</th>
                  <th>Subject</th>
                  <th>Classifier notes</th>
                  <th>Verdict</th>
                  <th>Decided</th>
                  <th>Received</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const auth = row.sender_auth ? SENDER_AUTH[row.sender_auth] : null;
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="font-mono text-sm text-ink">
                          {row.sender || "—"}
                        </div>
                        <div className="text-xs text-ink-low">
                          {row.mailbox_address || row.message_id}
                          {auth ? (
                            <span className={auth.className}> · {auth.label}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="text-sm text-ink max-w-72">
                        <div className="truncate" title={row.subject ?? undefined}>
                          {row.subject || "—"}
                        </div>
                      </td>
                      <td className="text-sm text-ink-medium">
                        {row.classifier_notes || "—"}
                      </td>
                      <td>
                        <Pill value={row.verdict} tone={verdictTone(row.verdict)} />
                      </td>
                      <td className="text-sm text-ink-low tnum">
                        {row.decided_by_user_id
                          ? formatRelative(row.decided_at)
                          : "by classifier"}
                      </td>
                      <td className="text-sm text-ink-low tnum">
                        {row.received_at
                          ? formatRelative(row.received_at)
                          : formatRelative(row.created_at)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {row.verdict !== "cleared" ? (
                            <ReleaseMenu row={row} onRelease={onRelease} />
                          ) : null}
                          {row.verdict !== "cleared" && !row.decided_by_user_id ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => onConfirm(row)}
                              title="Keep it withheld and take it off the queue"
                            >
                              Confirm
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm btn-danger"
                            onClick={() => onBlacklist(row)}
                          >
                            Blacklist
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>

      <SenderLedger />
    </>
  );
}

/**
 * Release, with the choice of what the gate learns from it. A domain is
 * offered only when the row has a sender to take it from.
 */
function ReleaseMenu({
  row,
  onRelease,
}: {
  row: EmailTrustRow;
  onRelease: (row: EmailTrustRow, trust: EmailReleaseTrust) => void;
}) {
  const menu = useMenuTrigger();
  const domain = domainOf(row.sender);
  return (
    <>
      <button {...menu.triggerProps} className="btn btn-ghost btn-sm">
        Release ▾
      </button>
      <Menu
        open={menu.open}
        onClose={() => menu.setOpen(false)}
        anchorRef={menu.triggerRef}
        id={menu.menuId}
        aria-label="Release"
        placement="bottom-end"
        focus={menu.edge}
      >
        {row.sender ? (
          <MenuItem onSelect={() => onRelease(row, "sender")}>
            Release & trust sender
          </MenuItem>
        ) : null}
        {domain ? (
          <MenuItem onSelect={() => onRelease(row, "domain")}>
            Release & trust <span className="font-mono">@{domain}</span>
          </MenuItem>
        ) : null}
        <MenuItem onSelect={() => onRelease(row, "none")}>
          Release this message only
        </MenuItem>
      </Menu>
    </>
  );
}

const LEDGER_FILTERS: { value: SenderTrustStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "trusted", label: "Trusted" },
  { value: "blocked", label: "Blocked" },
];

/**
 * What the gate has learned from release/blacklist decisions. Revoking an
 * entry sends that sender's next message back through the classifier.
 */
function SenderLedger() {
  const toast = useToast();
  const confirm = useConfirm();
  const [status, setStatus] = useUrlFilter<SenderTrustStatus | "all">(
    "ledger",
    LEDGER_FILTERS.map((f) => f.value),
    "all",
  );
  const sendersQuery = adminApi.emailTrust.senders(
    status === "all" ? undefined : status,
  );
  const revokeMutation = adminApi.emailTrust.revokeSender();

  const onRevoke = async (row: EmailSenderTrustRow) => {
    const ok = await confirm({
      title: `Revoke the ledger entry for ${row.sender_email}?`,
      body: "Their next message goes through the classifier again.",
      confirmLabel: "Revoke",
      tone: "danger",
    });
    if (!ok) return;
    revokeMutation.mutate(row.id, {
      onSuccess: () =>
        toast.ok(`Revoked ${row.sender_email} — next mail is classified again`),
      onError: (e) => toast.error(e),
    });
  };

  return (
    <section className="mt-10">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <h2 className="text-base font-semibold text-ink mr-2">Sender ledger</h2>
        <FilterChips
          aria-label="Sender ledger status"
          options={LEDGER_FILTERS}
          value={status}
          onChange={setStatus}
        />
        <div className="text-sm text-ink-low ml-2">
          <span className="tnum font-medium text-ink">
            {sendersQuery.data?.length ?? 0}
          </span>{" "}
          senders
        </div>
      </div>

      <QueryBoundary
        query={sendersQuery}
        empty={
          <div className="empty-state">
            <div className="title">Nothing learned yet</div>
            <p className="hint">
              Releasing an email trusts its sender; blacklisting blocks them.
              Entries appear here.
            </p>
          </div>
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Sender</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Decided</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-mono text-sm text-ink">
                      {row.sender_email}
                    </td>
                    <td>
                      <Pill
                        value={row.status}
                        tone={row.status === "trusted" ? "ok" : "alert"}
                      />
                    </td>
                    <td className="text-sm text-ink-medium">{row.source}</td>
                    <td className="text-sm text-ink-low tnum">
                      {formatRelative(row.updated_at)}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => onRevoke(row)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </section>
  );
}
